import React, { useState, useContext, useRef } from 'react';
import './NewConversationModal.css';
import Modal from '../Modal/Modal';
import TextInput from '../TextInput/TextInput';
import CheckboxInput from '../CheckboxInput/CheckboxInput';
import TextAreaInput from '../TextAreaInput/TextAreaInput';
import ActionButtons from '../ActionButtons/ActionButtons';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import baseApi from '../../common/api/base';
import logComponent from '../../common/logComponent';
import lowerCaseFirstLetter from '../../common/lowerCaseFirstLetter';

export default function NewConversationModal({ onSuccess, hide, isVisible  }) {
  const environmentContext = useContext(EnvironmentContext);
  const { activeUser: user, hasToken, selectedMonitoredIndividual } = environmentContext;
  const [status, setStatus] = useState('initial');
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [formIsInvalid, setFormIsInvalid] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [newConversation, setNewConversation] = useState({
    message: null,
    subject: null,
    isPrivate: null
  });
  const newConversationRef = useRef({
    message: null,
    subject: null,
    isPrivate: null
  });
  const actionButtons = useRef(null);
  const modalRef = useRef(null);
  const firstTabRef = useRef(null);

  if (logComponent('NewConversationModal')) { console.debug('NewConversationModal rendered'); }


  const setNewConversationRef = (el) => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal newConversationRef'); }
    newConversationRef.current = el;
  }


  const setModalRef = (el) => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal setModalRef'); }
    modalRef.current = el;
  }

  const setFirstTabRef = (el) => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal setFirstTabRef'); }
    firstTabRef.current = el;
  }

  const validateField = (field) => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal validateField'); }
    let newConversationThis = { ...newConversationRef.current }
    newConversationThis[lowerCaseFirstLetter(field.name)] = field;
    setNewConversationRef(newConversationThis);
    setNewConversation(newConversationThis);
  }

  const submit = (e) => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal submit'); }
   const requiredFieldList = ['message','subject'];

    let isValidThis = requiredFieldList.reduce((prev, curr) => prev && newConversationRef.current[curr] && newConversationRef.current[curr].isValid, true);

    if (isValidThis) {
      handleValidSubmission();
    } else {
      handleInvalidSubmission();
    }
  }

  const showValidationErrors = () => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal showValidationErrors'); }
    setShowErrors(true);
  }

  const hideValidationErrors = () => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal hideValidationErrors'); }
    setShowErrors(false);
  }  

  const handleValidSubmission = () => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal handleValidSubmission'); }
    setIsValid(true);
    hideValidationErrors();

    createNewConversation();
  }

  const handleInvalidSubmission = () => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal handleInvalidSubmission'); }
    showValidationErrors();
    modalRef.current.scrollTop = 0;
    firstTabRef.current.focus();    

    setFormIsInvalid(true);

  }

  const startModalExit = () => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal startModalExit'); }
    setModalShouldExit(true);
  }

  const createNewConversation = () => {
    if (logComponent('NewConversationModal')) { console.debug('NewConversationModal createNewConversation'); }
    if (!hasToken) return;

    setStatus('pending');

    const messageToPost = {
      message: newConversationRef.current.message.value,
      createdByUserId: user.userId,
      userMessageThread: {
        subject: newConversationRef.current.subject.value,
        isPrivate: newConversationRef.current.isPrivate.value,
        monitoredIndividualId: selectedMonitoredIndividual.monitoredIndividualId,
        facilityId: selectedMonitoredIndividual.lastKnownFacilityId,
        createdByUserId: user.userId
      }
    };

    baseApi.UserMessage.Create(messageToPost)
      .then(
        result => {
          setStatus('success');
          onSuccess(result.data);
          startModalExit();
        },
        error => {
          setStatus('failure');
        }
      )
  }



  return (
    <Modal isVisible={isVisible} shouldExit={modalShouldExit} hide={hide} ref={setModalRef} remainOnBackdropClick={true}>
      <div className="New-Conversation-Modal">
        <h3 className="New-Conversation-Modal__heading">New conversation</h3>
        <p className={'New-Conversation-Modal__page-error' + (showErrors ? ' visible' : '')}>There were some errors. Please double check the information and resubmit.</p>
        <p className={'New-Conversation-Modal__page-error' + (status === 'failure' ? ' visible' : '')}>Sorry, we were unable to complete the action.  Conversation was not created.  Please try again.</p>
        <form className="New-Conversation-Modal__form">
          <TextInput
            label="Subject"
            name="subject"
            rules={{ required: true }}
            maxlength={100}
            errorMessages={{ required: 'Please provide a subject for the conversation.' }}
            showError={formIsInvalid}
            ref={setFirstTabRef}
            onValidate={validateField} />
          <CheckboxInput
            label="Direct message"
            name="isPrivate"
            instructions="Note: when checked, this message will be a direct conversation between you and the facility.  No other family member will be able to read this conversation."
            explanation="Make this a direct message"
            initialValue={false}
            onValidate={validateField} />
          <TextAreaInput
            label="Message"
            name="message"
            placeholder="Type your message here..."
            rules={{ required: true }}
            showError={formIsInvalid}
            errorMessage="Please provide a message."
            onValidate={validateField} />
          <ActionButtons
            ref={actionButtons}
            mainAction={submit}
            mainActionText="Send message"
            mainActionIcon="arrow-right"
            cancel={startModalExit}
            cancelText="Cancel"
            currentStatus={status} />
        </form>
      </div>
    </Modal>
  );
}